import { guestAppConfig } from '@tov-plus/config';
import * as Sentry from '@sentry/browser';
import { useEffect } from 'react';
import {
  createTovRouter,
  NavLink,
  useLocation,
  useRouteError,
} from '@tov-plus/frontend-data';
import { AppShell } from '../components/AppShell';
import {
  OfflineBanner,
  OfflineRetryButton,
  OfflineStatePanel,
} from '@tov-plus/pwa';
import { GuestOpeningExperienceGate } from '../components/GuestOpeningExperienceGate';
import { StatePanel } from '../components/StatePanel';
import {
  GuestAccessProvider,
  useGuestAccess,
} from '../contexts/GuestAccessContext';
import { AccessPage } from '../pages/AccessPage';
import { EventDetailPage } from '../pages/EventDetailPage';
import { EventsPage } from '../pages/EventsPage';
import { FormsPage } from '../pages/FormsPage';
import { GalleryPage } from '../pages/GalleryPage';
import { GallerySharePage } from '../pages/GallerySharePage';
import { HelpPage } from '../pages/HelpPage';
import { FollowUpPage } from '../pages/FollowUpPage';
import { HomePage } from '../pages/HomePage';
import { LocationsPage } from '../pages/LocationsPage';
import { MessagesPage } from '../pages/MessagesPage';
import { MyInvitePage } from '../pages/MyInvitePage';
import { NotFoundPage } from '../pages/NotFoundPage';
import { OpenInvitationPlaygroundPage } from '../pages/OpenInvitationPlaygroundPage';
import { PublicInviteJoinPage } from '../pages/PublicInviteJoinPage';
import { RegistryPage } from '../pages/RegistryPage';
import { RecentInvitesPage } from '../pages/RecentInvitesPage';
import { ReviewerPreviewPage } from '../pages/ReviewerPreviewPage';
import { RsvpPage } from '../pages/RsvpPage';
import { SchedulePage } from '../pages/SchedulePage';
import { SearchPage } from '../pages/SearchPage';
import { SeatingPage } from '../pages/SeatingPage';
import { SharedPlanningPage } from '../pages/SharedPlanningPage';
import { TodayPage } from '../pages/TodayPage';
import { TravelPacketPage } from '../pages/TravelPacketPage';
import {
  resolveGuestRouterBasename,
  shouldRedirectMissingSlugToMarketing,
} from './access';
import { guestEnv } from '../config/env';
import { PageLayout, msg, TovButton } from '@tov-plus/ui';
function AccessGate() {
  const access = useGuestAccess();
  const location = useLocation();

  useEffect(() => {
    if (
      access.status !== 'missing_slug' ||
      typeof window === 'undefined' ||
      !shouldRedirectMissingSlugToMarketing(window.location.pathname)
    ) {
      return;
    }

    const landingUrl = new URL(guestEnv.marketingSiteUrl).toString();

    if (window.location.href !== landingUrl) {
      window.location.replace(landingUrl);
    }
  }, [access.status]);

  if (access.status === 'loading') {
    return (
      <div className="guest-standalone">
        <OfflineBanner />
        <StatePanel preset="loading" />
      </div>
    );
  }
  if (access.status === 'missing_slug') {
    return (
      <div className="guest-standalone">
        <StatePanel preset="loading" />
      </div>
    );
  }
  if (access.status === 'missing_token') {
    if (
      access.reviewPass?.isValid &&
      access.occasionSlug &&
      location.pathname === '/review'
    ) {
      return (
        <ReviewerPreviewPage
          occasionSlug={access.occasionSlug}
          reviewPass={access.reviewPass}
        />
      );
    }
    return (
      <div className="guest-standalone">
        <StatePanel
          preset="access-unavailable"
          actions={
            <>
              {access.reviewPass?.isValid ? (
                <NavLink className="primary-button" to="/review">
                  {msg('app.router.app_router_open_reviewer_preview')}{' '}
                </NavLink>
              ) : null}
              <a className="primary-button" href={guestAppConfig.marketingUrl}>
                {msg('app.router.app_router_open_tov')}{' '}
              </a>
              <a
                className="secondary-link"
                href={`mailto:${guestAppConfig.supportEmail}?subject=Guest%20invite%20help`}
              >
                {msg('app.router.app_router_contact_support')}{' '}
              </a>
            </>
          }
          detail={`${access.errorMessage} Ask the hosts to resend the latest invite so you open the experience with the correct occasion and token context.`}
        />
      </div>
    );
  }
  if (access.status === 'invalid_token') {
    const recoveryBody = `Occasion: ${access.occasionSlug ?? 'unknown'}\nIssue: Please send the latest valid invite link.`;
    return (
      <div className="guest-standalone">
        <main>
          <PageLayout
            title={msg('app.router.app_router_invite_link_expired_or_invalid')}
          >
            <section
              className="state-panel state-panel--critical"
              data-testid="guest-invalid-invite-fallback"
            >
              <p>{access.errorMessage}</p>
              <p>
                {msg(
                  'app.router.app_router_if_you_opened_an_older_email_a_forwarded_message_or_a_partial_link_clear_the_saved_invite_here_and_reopen_the_latest_host_provided_link',
                )}
              </p>
              <div className="state-panel__actions">
                <TovButton
                  className="primary-button"
                  onClick={() => access.clearStoredToken()}
                >
                  {msg('app.router.app_router_clear_saved_invite')}{' '}
                </TovButton>
                <a
                  className="secondary-link"
                  href={`mailto:${guestAppConfig.supportEmail}?subject=${encodeURIComponent('Request latest invite')}&body=${encodeURIComponent(recoveryBody)}`}
                >
                  {msg('app.router.app_router_request_latest_invite')}{' '}
                </a>
                <a
                  className="secondary-link"
                  href={`mailto:${guestAppConfig.supportEmail}?subject=Guest%20invite%20recovery`}
                >
                  {msg('app.router.app_router_contact_support')}{' '}
                </a>
              </div>
            </section>
          </PageLayout>
        </main>
      </div>
    );
  }
  if (access.status === 'occasion_unavailable') {
    return (
      <div className="guest-standalone">
        <StatePanel
          preset="unknown-error"
          actions={
            <>
              <TovButton
                className="primary-button"
                onClick={() => access.clearStoredToken()}
              >
                {msg('app.router.app_router_clear_saved_invite')}{' '}
              </TovButton>
              <a
                className="secondary-link"
                href={`mailto:${guestAppConfig.supportEmail}?subject=${encodeURIComponent('Occasion link unavailable')}`}
              >
                {msg('app.router.app_router_request_updated_link')}{' '}
              </a>
              <a className="secondary-link" href={guestAppConfig.marketingUrl}>
                {msg('app.router.app_router_open_tov')}{' '}
              </a>
            </>
          }
          detail={access.errorMessage}
        />
      </div>
    );
  }
  if (access.status === 'error') {
    return (
      <div
        className="guest-standalone"
        data-testid="guest-invalid-invite-fallback"
      >
        <StatePanel
          preset="access-unavailable"
          actions={
            <>
              <TovButton
                className="primary-button"
                onClick={() => access.refresh()}
              >
                {msg('general.actions.retry')}{' '}
              </TovButton>
              <TovButton
                className="secondary-link"
                onClick={() => access.clearStoredToken()}
              >
                {msg('app.router.app_router_clear_saved_invite')}{' '}
              </TovButton>
              <a
                className="secondary-link"
                href={`mailto:${guestAppConfig.supportEmail}?subject=Guest%20invite%20recovery`}
              >
                {msg('app.router.app_router_contact_support')}{' '}
              </a>
            </>
          }
          detail={`${access.errorMessage} Retry first. If the same problem continues, reopen the invite link or clear the saved invite and start again.`}
        />
      </div>
    );
  }
  if (access.status === 'offline') {
    return (
      <div className="guest-standalone">
        <OfflineBanner />
        <OfflineStatePanel
          action={<OfflineRetryButton onRetry={() => access.refresh()} />}
          detail={access.errorMessage}
        />
      </div>
    );
  }
  return (
    <GuestOpeningExperienceGate>
      <AppShell />
    </GuestOpeningExperienceGate>
  );
}
function ReviewerPreviewRoute() {
  const access = useGuestAccess();
  if (
    access.status === 'ready' &&
    access.reviewPass?.isValid &&
    access.occasionSlug
  ) {
    return (
      <ReviewerPreviewPage
        localeCode={access.experience.localizationContext.resolvedLocaleCode}
        occasionSlug={access.occasionSlug}
        reviewPass={access.reviewPass}
        timezone={access.experience.occasion.timezone}
      />
    );
  }
  return (
    <StatePanel
      preset="unknown-error"
      actions={
        <NavLink className="primary-button" to="/access">
          {msg('app.router.app_router_review_access')}{' '}
        </NavLink>
      }
    />
  );
}
function RouterErrorBoundary() {
  const routeError = useRouteError();
  useEffect(() => {
    if (!routeError) {
      return;
    }
    Sentry.captureException(routeError);
  }, [routeError]);
  return (
    <div className="guest-standalone">
      <StatePanel
        preset="unknown-error"
        actions={
          <NavLink className="primary-button" to="/">
            {msg('app.router.app_router_return_to_guest_overview')}{' '}
          </NavLink>
        }
      />
    </div>
  );
}
export const router = createTovRouter(
  [
    {
      path: '/invite/delivery/:deliveryToken',
      element: <PublicInviteJoinPage />,
      errorElement: <RouterErrorBoundary />,
    },
    {
      path: '/invite/:token',
      element: <PublicInviteJoinPage />,
      errorElement: <RouterErrorBoundary />,
    },
    {
      path: '/gallery/share/:shareToken',
      element: <GallerySharePage />,
      errorElement: <RouterErrorBoundary />,
    },
    ...(import.meta.env.DEV
      ? [
          {
            path: '/__open-invitation-playground',
            element: <OpenInvitationPlaygroundPage />,
            errorElement: <RouterErrorBoundary />,
          },
        ]
      : []),
    {
      path: '/',
      element: (
        <GuestAccessProvider>
          <AccessGate />
        </GuestAccessProvider>
      ),
      errorElement: <RouterErrorBoundary />,
      children: [
        {
          index: true,
          element: <HomePage />,
        },
        {
          path: 'events',
          element: <EventsPage />,
        },
        {
          path: 'events/:eventId',
          element: <EventDetailPage />,
        },
        {
          path: 'search',
          element: <SearchPage />,
        },
        {
          path: 'today',
          element: <TodayPage />,
        },
        {
          path: 'messages',
          element: <MessagesPage />,
        },
        {
          path: 'my-invite',
          element: <MyInvitePage />,
        },
        {
          path: 'recent-invites',
          element: <RecentInvitesPage />,
        },
        {
          path: 'review',
          element: <ReviewerPreviewRoute />,
        },
        {
          path: 'locations',
          element: <LocationsPage />,
        },
        {
          path: 'travel-packet',
          element: <TravelPacketPage />,
        },
        {
          path: 'rsvp',
          element: <RsvpPage />,
        },
        {
          path: 'forms',
          element: <FormsPage />,
        },
        {
          path: 'registry',
          element: <RegistryPage />,
        },
        {
          path: 'gallery',
          element: <GalleryPage />,
        },
        {
          path: 'follow-up',
          element: <FollowUpPage />,
        },
        {
          path: 'schedule',
          element: <SchedulePage />,
        },
        {
          path: 'shared-planning',
          element: <SharedPlanningPage />,
        },
        {
          path: 'seating',
          element: <SeatingPage />,
        },
        {
          path: 'help',
          element: <HelpPage />,
        },
        {
          path: 'access',
          element: <AccessPage />,
        },
        {
          path: '*',
          element: <NotFoundPage />,
        },
      ],
    },
  ],
  {
    basename:
      typeof window === 'undefined'
        ? undefined
        : resolveGuestRouterBasename(window.location.pathname),
  },
);
